/**
 * Currently-selected contact - a persistent, cross-page "who am I acting
 * on behalf of right now" concept (per Todd 2026-11-03), so the Map's
 * Alert action can target a specific contact without prompting every
 * time. Persistent (survives navigation) rather than one-shot like
 * state/messagingIntent.ts, since an agent may click through several map
 * items in a row for the same contact - deliberately NOT localStorage-
 * backed though, matching ListDrawer.selectedListId's in-memory-only
 * precedent (apps/agent/src/components/ListDrawer.ts).
 *
 * Caches the selected contact's current alert set (real + pending) once,
 * on selection, so isContactAlerted() can be a synchronous cache read -
 * same shape as CartDrawer.hasItem()'s instant lookup
 * (apps/agent/src/components/CartDrawer.ts) rather than an async call per
 * button render.
 */
import type { AgentClient } from '@slowmaui/api-client';

export interface SelectedContact {
  id: string;
  name: string | null;
  email: string;
  userId: string | null;
}

export type AlertItemType = 'property' | 'neighborhood';

let selected: SelectedContact | null = null;
let alertedKeys: Set<string> | null = null;
const listeners = new Set<() => void>();

/** Both the header badge (main.ts) and Contacts.ts's row/Actions-card
 *  highlighting need to react to selection changes independently, so this
 *  is a Set, not a single slot - returns an unsubscribe function. */
export function onSelectedContactChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notify(): void {
  listeners.forEach((cb) => cb());
}

export function getSelectedContact(): SelectedContact | null {
  return selected;
}

function alertKey(itemId: string, itemType: AlertItemType): string {
  return `${itemType}:${itemId}`;
}

export async function selectContact(contact: SelectedContact, client: AgentClient): Promise<void> {
  selected = contact;
  alertedKeys = new Set();
  notify();
  try {
    const detail = await client.getContact(contact.id);
    alertedKeys = new Set([
      ...detail.alerts.map((a) => alertKey(a.itemId, a.itemType as AlertItemType)),
      ...detail.pendingAlerts.map((a) => alertKey(a.itemId, a.itemType as AlertItemType)),
    ]);
  } catch (error) {
    console.error('[selectedContact] Failed to load contact alerts:', error);
  }
}

export function clearSelectedContact(): void {
  selected = null;
  alertedKeys = null;
  notify();
}

export function isContactAlerted(itemId: string, itemType: AlertItemType): boolean {
  return !!alertedKeys?.has(alertKey(itemId, itemType));
}

/** Mirrors CartDrawer.toggleItem(): add or remove depending on current
 *  state, then update the cache. Returns whether the write that just
 *  landed was a pending alert (no account yet) vs a real one, so the
 *  caller can surface that distinction - or undefined if nothing is
 *  selected (caller should have gated the button via isLoggedIn already). */
export async function toggleContactAlert(
  client: AgentClient,
  itemId: string,
  itemType: AlertItemType
): Promise<{ alerted: boolean; pending: boolean } | undefined> {
  if (!selected) return undefined;
  const key = alertKey(itemId, itemType);
  const wasAlerted = alertedKeys?.has(key) ?? false;
  try {
    if (wasAlerted) {
      await client.removeContactAlert(selected.id, itemId);
      alertedKeys?.delete(key);
      return { alerted: false, pending: false };
    } else {
      const result = await client.addContactAlert(selected.id, { itemId, itemType });
      alertedKeys?.add(key);
      return { alerted: true, pending: result.pending };
    }
  } catch (error) {
    console.error('[selectedContact] Failed to toggle alert:', error);
    return undefined;
  }
}
